import type { User } from "../types/Types";
import {
  isBusinessOrExecutiveMembership,
  isPaidPortalMember,
  isYouthOrEarlyStageMembership,
} from "../utils/membershipTier";

/** Stored on Users.MembershipName for unpaid Explorers. */
export const EXPLORER_MEMBERSHIP_NAME = "Explorer";

/** Business, Executive, Early Stage, or Youth membership names. */
export function hasRecognizedSmbfMembership(
  membershipName?: string | null,
): boolean {
  return (
    isYouthOrEarlyStageMembership(membershipName) ||
    isBusinessOrExecutiveMembership(membershipName)
  );
}

/**
 * Explorer by membership name only (no payment checks):
 * empty or "Explorer" → Explorer.
 */
export function isExplorerMembershipName(
  membershipName?: string | null,
): boolean {
  const name = (membershipName ?? "").trim();
  if (!name) return true;
  return name.toLowerCase() === EXPLORER_MEMBERSHIP_NAME.toLowerCase();
}

/** Canceled or incomplete_expired — renew / inactive flows (Stripe signal). */
export function isCanceledMembership(user: User | null | undefined): boolean {
  if (!user) return false;
  const stripe = (user.stripeStatus ?? "").trim().toLowerCase();
  return (
    stripe === "canceled" ||
    stripe === "cancelled" ||
    stripe === "incomplete_expired"
  );
}

/**
 * Active paid SMBF member for billing (HasPaid / Stripe).
 * Prefer membership-name identity for Explorer vs member product gates.
 */
export function isPaidSmbfMember(user: User | null | undefined): boolean {
  if (!user) return false;
  if (!isPaidPortalMember(user)) return false;
  return (
    !user.membershipName?.trim() ||
    hasRecognizedSmbfMembership(user.membershipName)
  );
}

/**
 * Explorer product identity — membership name only.
 * Prefer `useCurrentMembership()` / `resolveMembershipIdentity()` in UI.
 */
export function isUnpaidExplorerUser(user: User | null | undefined): boolean {
  if (!user) return false;
  return isExplorerMembershipName(user.membershipName);
}
